import type { ReactNode } from "react";
import { getMindMapPreviews, getSubjectOverview } from "../../api/queries.js";
import { useQuery } from "../../api/useQuery.js";
import type { MindMapAggregateDto } from "@mmm/contracts";
import { Card, ErrorState, useT } from "@mmm/ui";
import { MapDiagram } from "../maps/MapDiagram.js";
interface StudyNotesPreviewProps {
  subjectId: string;
  chapterId?: string;
  includeChapters?: boolean;
}

interface PreviewSection {
  title: string;
  mapId?: string;
  level: 1 | 2;
}

function inlineMarkdown(text: string): ReactNode[] {
  return text.split(/(\*\*.+?\*\*)/g).map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return part;
  });
}

function writingBlocks(markdown: string): ReactNode[] {
  const result: ReactNode[] = [];
  let listItems: Array<{ text: string; key: number }> = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    result.push(
      <ul key={`list-${listItems[0].key}`}>
        {listItems.map((item) => <li key={item.key}>{inlineMarkdown(item.text)}</li>)}
      </ul>
    );
    listItems = [];
  };

  markdown.split(/\r?\n/).forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }
    const heading = trimmed.match(/^#{1,3}\s+(.*)$/);
    if (heading) {
      flushList();
      result.push(<h4 key={index}>{inlineMarkdown(heading[1])}</h4>);
      return;
    }
    const listItem = trimmed.match(/^[-*]\s+(.*)$/);
    if (listItem) {
      listItems.push({ text: listItem[1], key: index });
      return;
    }
    flushList();
    result.push(<p key={index}>{inlineMarkdown(trimmed)}</p>);
  });
  flushList();
  return result;
}

function sortedNodes(aggregate: MindMapAggregateDto) {
  return aggregate.nodes.slice().sort((a, b) => a.y - b.y || a.x - b.x || a.id.localeCompare(b.id));
}

export function StudyNotesPreview({ subjectId, chapterId, includeChapters = true }: StudyNotesPreviewProps) {
  const t = useT();
  const overview = useQuery(getSubjectOverview, { subjectId });
  const subject = overview.data?.subject;
  const chapter = chapterId ? overview.data?.chapters.find((item) => item.id === chapterId) : undefined;

  let sections: PreviewSection[] = [];
  let documentTitle = subject?.title ?? "";
  if (subject) {
    if (chapterId) {
      documentTitle = chapter?.title ?? subject.title;
      const map = overview.data?.mindMaps.find((item) => item.chapterId === chapterId);
      sections = chapter ? [{ title: chapter.title, mapId: map?.id, level: 1 }] : [];
    } else {
      const subjectMap = overview.data?.mindMaps.find((item) => item.chapterId === null);
      sections = [{ title: subject.title, mapId: subjectMap?.id, level: 1 }];
      if (includeChapters) {
        sections.push(
          ...(overview.data?.chapters ?? [])
            .slice()
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((item) => ({
              title: item.title,
              mapId: overview.data?.mindMaps.find((map) => map.chapterId === item.id)?.id,
              level: 2 as const,
            }))
        );
      }
    }
  }

  const mapIds = sections.flatMap((section) => (section.mapId ? [section.mapId] : []));
  const maps = useQuery(getMindMapPreviews, { mindMapIds: mapIds }, { enabled: mapIds.length > 0 });

  if (!overview.data && overview.error && !overview.waiting) {
    return <ErrorState variant="panel" title={t("subjects.loadOneFailed")} onRetry={overview.retry} />;
  }
  if (!overview.data || (mapIds.length > 0 && !maps.data && maps.error && !maps.waiting)) {
    return !overview.data ? (
      <p className="mmm-export-preview-loading" role="status">{t("common.loading")}</p>
    ) : (
      <ErrorState variant="panel" title={t("maps.loadFailed")} onRetry={maps.retry} />
    );
  }
  if (mapIds.length > 0 && !maps.data) {
    return <p className="mmm-export-preview-loading" role="status">{t("common.loading")}</p>;
  }

  const mapById = new Map((maps.data ?? []).map((aggregate) => [aggregate.mindMap.id, aggregate]));

  return (
    <Card padding="md" className="mmm-export-preview-page">
      <h1 className="mmm-export-document-title">{documentTitle}</h1>
      {sections.map((section, sectionIndex) => {
        const aggregate = section.mapId ? mapById.get(section.mapId) : undefined;
        const nodes = aggregate ? sortedNodes(aggregate) : [];
        return (
          <section
            key={`${section.title}-${sectionIndex}`}
            className={`mmm-export-document-section mmm-export-document-section--level-${section.level}`}
          >
            {sectionIndex > 0 && <h2>{section.title}</h2>}
            {aggregate && <MapDiagram aggregate={aggregate} />}
            {aggregate?.mindMapMarkdown.trim() && (
              <div className="mmm-export-document-writing">
                {writingBlocks(aggregate.mindMapMarkdown)}
              </div>
            )}
            {nodes.map((node) => (
              <div className="mmm-export-document-node" key={node.id}>
                {section.level === 1 ? <h2>{node.title}</h2> : <h3>{node.title}</h3>}
                <div className="mmm-export-document-writing">
                  {node.writing.markdown.trim()
                    ? writingBlocks(node.writing.markdown)
                    : <p>{t("maps.noNotesYet")}</p>}
                </div>
              </div>
            ))}
          </section>
        );
      })}
    </Card>
  );
}
